/**
 * Aufgabenkern — EINE Ableitung fuer Aufgabenliste (TasksView) und Dashboard.
 *
 * Bis 25.09.2026 rechnete die Aufgabenliste ihre Zeilen in zwei `useMemo`s
 * selbst, und das Dashboard zaehlte in einer eigenen Kurzfassung nach. Zwei
 * Stellen, die dieselbe Frage beantworten, driften: die Kachel zeigte frueher
 * oder spaeter eine andere Zahl als die Liste dahinter. Jetzt liegt die
 * Ableitung hier, wortgetreu aus TasksView uebernommen — ohne React, damit
 * sie ohne jsdom pruefbar ist. Wer die Regeln aendert, aendert sie fuer
 * beide Ansichten zugleich.
 *
 * Reihenfolge des Aufrufs (wie in TasksView):
 *   1. `selectTaskProjects(projekte, nutzer)` — Uebungsprojekte raus,
 *      nur Verantwortung/Vertretung, je Projektnummer die juengste Zeile.
 *   2. `buildTaskGroups(projekte, heute)` — Dokumentpflichten und
 *      ueberfaellige Begehungen je Projekt, dringendstes zuerst.
 */
import { ServiceItem, ReportMetadata, ServiceTransaction } from '../types';
import { isAccountingEntry } from './serviceLogUtils';
import { inspectionProofMatcher, isFlatRatePosition } from './projectLv';
import { istSifaProjekt } from './projectType';
import { getProjectRelation } from './projectAccess';
import { istUebungsprojekt } from './uebung/uebungService';
import { isTaskDocumentAssignment, taskDocumentLabel } from './taskDocuments';
import type { ListProject } from './listProject';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type Priority = 'urgent' | 'soon' | 'normal';
export type TaskType = 'document' | 'inspection_overdue';

export interface Task {
    id: string;
    serviceDbId?: string; // UUID from project_services table for NEP toggle
    label: string;
    type: TaskType;
    priority: Priority;
    detail?: string;
    daysInfo?: number;
    assignment?: string;
}

/**
 * Eine LV-Zeile, wie die Ableitung sie liest. Bevorzugt die DB-Zeile aus
 * `project_services` (snake_case, mit `is_optional`); ohne DB-Zeilen faellt
 * die Ableitung auf `globalData.services` zurueck — camelCase-Positionen, auf
 * denen die snake_case-Mengen fehlen (dann gilt 0, siehe `Number(...) || 0`).
 */
export interface TaskServiceRow {
    id?: string;
    assignment?: string | null;
    title?: string | null;
    quantity_contracted?: number | string | null;
    quantity_used?: number | string | null;
    is_optional?: boolean | null;
    flat_rate?: unknown;
    flatRate?: unknown;
}

/**
 * Was die Ableitung an einem Projekt liest — alles davon traegt der
 * Listeneintrag (`ListProject`). Bis auf `id` optional, damit Aufgabenliste,
 * Dashboard und Testfixtures mit Teilobjekten passen.
 */
export type TaskProject = Pick<ListProject, 'id'>
    & Partial<Pick<ListProject,
        'name' | 'status' | 'user_id' | 'transfer_target_id' | 'project_number'
        | 'start_date' | 'last_inspection_date' | 'updated_at' | 'coordinator_name'>>
    & {
        project_services?: ReadonlyArray<TaskServiceRow> | null;
        data?: { metadata?: ReportMetadata | null } | null;
    };

export interface ProjectTaskGroup {
    // Der schmale Typ, nicht der des Aufrufers: `ergaenzePlanungAufgaben`
    // (planungAufgaben.ts) nimmt und liefert `ProjectTaskGroup[]` ohne
    // Typparameter, ein generisches `P` ginge dort verloren. Wer die Gruppe
    // oeffnet, bekommt deshalb nur `TaskProject` — App.handleOpenProject
    // sucht die Listenzeile ohnehin per `id` heraus.
    project: TaskProject;
    projectName: string;
    projectNumber: string;
    coordinator: string;
    startDate: string | null;
    tasks: Task[];
    dismissedTasks: Task[];
    urgentCount: number;
    soonCount: number;
}

/** Nutzerangaben, die fuer die Zuordnung zaehlen (wie `getProjectRelation`). */
export interface TaskUser {
    id?: string | null;
    email?: string | null;
}

export const PRIORITY_ORDER: Record<Priority, number> = { urgent: 0, soon: 1, normal: 2 };

/** Heute, 00:00 Ortszeit — der Bezugspunkt aller Tagesrechnungen. */
export const startOfToday = (): Date => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
};

// ═══════════════════════════════════════════════════════════════════
// 1. PROJEKTAUSWAHL — Verantwortung, Vertretung, juengste Zeile je Nummer
// ═══════════════════════════════════════════════════════════════════

/**
 * ÜBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG (docs/lernzentrum-tutorial-plan.md,
 * 1.11 und 8.2). Gefiltert wird hier und nicht beim Laden, weil die Liste als
 * Prop ankommt und dieselbe Liste zugleich die Projektverwaltung speist, die
 * Übungsprojekte sehr wohl zeigt.
 */
export const selectTaskProjects = <P extends TaskProject>(alleProjekte: ReadonlyArray<P>, userProfile: TaskUser | null | undefined): P[] => {
    const projects = alleProjekte.filter(p => !istUebungsprojekt(p));
    if (!userProfile?.id) return [];
    const myEmail = (userProfile.email || '').trim().toLowerCase();
    if (myEmail.length < 3) return projects.filter(p => p.user_id === userProfile.id);

    const relevant = projects.filter(p => {
        const meta = p.data?.metadata as ReportMetadata;
        if (!meta) return false;
        // Eigenes Projekt, Verantwortlicher aus der Vertreterliste oder
        // aktive Urlaubsvertretung (services/projectAccess.ts).
        const rel = getProjectRelation(p, userProfile);
        return rel.isResponsible || rel.isSubstitute;
    });

    // De-duplicate by projectId/projectNumber (keep latest)
    const numberToIdMap = new Map<string, string>();
    relevant.forEach(p => {
        const meta = p.data.metadata as ReportMetadata;
        const pid = meta.globalData?.projectId;
        const pnum = meta.projectNumber;
        if (pid && pnum) numberToIdMap.set(pnum, pid);
    });

    const groups: Record<string, P> = {};
    relevant.forEach(p => {
        const meta = p.data.metadata as ReportMetadata;
        const globalData = meta.globalData;
        const pnum = meta.projectNumber;
        let groupingKey = globalData?.projectId;
        if (!groupingKey && pnum && numberToIdMap.has(pnum)) groupingKey = numberToIdMap.get(pnum);
        const finalKey = groupingKey ? `uuid_${groupingKey}` : (pnum ? `num_${pnum}` : `id_${p.id}`);

        if (!groups[finalKey]) {
            groups[finalKey] = p;
        } else {
            const currentNr = meta.inspectionNumber || 0;
            const existingNr = groups[finalKey].data.metadata?.inspectionNumber || 0;
            if (currentNr > existingNr) groups[finalKey] = p;
            else if (currentNr === existingNr && new Date(p.updated_at) > new Date(groups[finalKey].updated_at)) groups[finalKey] = p;
        }
    });

    return Object.values(groups);
};

// ═══════════════════════════════════════════════════════════════════
// 2. TASK GENERATION — Focused on documents & real obligations
// ═══════════════════════════════════════════════════════════════════

/**
 * `user` (optional): fuer wen die Liste gerechnet wird. Nur damit faellt der
 * Begehungsbefund in Projekten weg, in denen der Nutzer NUR Verantwortliche
 * Planungsphase ist — die Dokumentpflichten (Vorankuendigung, Unterlage,
 * Planung) bleiben, die gehoeren gerade ihr. Ohne `user` wie bisher.
 */
export const buildTaskGroups = (
    groupedProjects: ReadonlyArray<TaskProject>,
    now: Date = startOfToday(),
    user?: TaskUser | null,
): ProjectTaskGroup[] => {
    const groups: ProjectTaskGroup[] = [];

    groupedProjects.forEach(p => {
        const meta = p.data?.metadata as ReportMetadata;
        if (!meta) return;
        const global = meta.globalData;
        const status = global?.status || p.status || 'planning';

        // Abgeschlossene und ruhende Projekte tragen keine Pflichten mehr.
        // Der versendete, aber nicht gebuchte Bericht stand frueher auch hier
        // (Betreiber-Entscheidung 20.09.2026: raus aus der Aufgabenliste) —
        // er lebt in der Meldungsliste der Verwaltung und im
        // Integritaetswaechter weiter, dort mit Nachbuchen und Ausnahme.
        if (status === 'finished' || status === 'completed' || status === 'on_hold') return;

        const services: ReadonlyArray<TaskServiceRow> = p.project_services || [];
        const jsonServices = (global?.services || []) as ServiceItem[];
        // Prefer DB services (has is_optional field). Fall back to JSON only if DB empty.
        const allServices: ReadonlyArray<TaskServiceRow> = services.length > 0 ? services : jsonServices;
        const usingDbServices = services.length > 0;

        // Debug: if no DB services available, NEP button won't work
        if (!usingDbServices && jsonServices.length > 0) {
            console.warn(`TasksView: Project "${p.name}" has no project_services from DB — NEP toggle won't work. Ensure project_services is joined in the query.`);
        }

        const startDateStr = global?.startDate || p.start_date;
        const startDate = startDateStr ? new Date(startDateStr) : null;
        const projectStarted = startDate ? now >= startDate : false;
        const daysUntilStart = startDate ? Math.ceil((startDate.getTime() - now.getTime()) / 864e5) : null;

        const tasks: Task[] = [];
        const dismissedTasks: Task[] = []; // NEP positions marked as optional

        // ─────────────────────────────────────────────────
        // 1. DOCUMENT OBLIGATIONS (assignment-based)
        // ─────────────────────────────────────────────────
        // Welche Dokument-Positionen hier als Aufgabe stehen, entscheidet
        // allein services/taskDocuments.ts — dieselbe Liste speist die
        // Aufgaben-Kachel im Dashboard. Seit 20.09.2026 nur noch
        // Vorankündigung, Unterlage für spätere Arbeiten und Planungsphasen;
        // SiGe-Plan, A+S-Plan, Einweisung und Baustellenordnung haben ihre
        // Kachel im Projekt-Hub und waren hier Dauerrauschen.
        allServices.forEach(s => {
            const assignment = (s.assignment || 'none') as string;
            if (!isTaskDocumentAssignment(assignment)) return;

            const contracted = Number(s.quantity_contracted) || 0;
            const used = Number(s.quantity_used) || 0;
            if (contracted <= 0) return;
            if (used >= contracted) return; // Already done

            const isOptional = s.is_optional === true;
            const docLabel = taskDocumentLabel(assignment, s.title);

            // Determine priority
            let priority: Priority = 'normal';
            let detail: string | undefined;
            let daysInfo: number | undefined;

            if (projectStarted) {
                const daysSinceStart = startDate ? Math.ceil((now.getTime() - startDate.getTime()) / 864e5) : 0;
                if (daysSinceStart > 14) {
                    priority = 'urgent';
                    detail = `Projekt läuft seit ${daysSinceStart} Tagen — Dokument fehlt`;
                    daysInfo = daysSinceStart;
                } else {
                    priority = 'soon';
                    detail = `Projekt gestartet — bitte zeitnah erstellen`;
                    daysInfo = daysSinceStart;
                }
            } else if (daysUntilStart !== null && daysUntilStart <= 14) {
                priority = 'soon';
                detail = `Projektstart in ${daysUntilStart} Tagen`;
                daysInfo = daysUntilStart;
            } else {
                priority = 'normal';
                if (daysUntilStart !== null) {
                    detail = `Projektstart in ${daysUntilStart} Tagen`;
                    daysInfo = daysUntilStart;
                }
            }

            const task: Task = {
                id: s.id || `doc-${assignment}`,
                serviceDbId: s.id, // UUID for NEP toggle
                label: docLabel,
                type: 'document',
                priority,
                detail: isOptional ? `NEP — ${detail || 'Eventualposition'}` : detail,
                daysInfo,
                assignment,
            };

            if (isOptional) {
                dismissedTasks.push(task);
            } else {
                tasks.push(task);
            }
        });

        // ─────────────────────────────────────────────────
        // 2. INSPECTION OVERDUE (no activity in > 2× interval)
        // ─────────────────────────────────────────────────
        const isReportExempt = global?.reportExempt === true;
        // Nur Verantwortliche Planungsphase: macht keine Begehungen, ein
        // „Keine Begehung seit X Tagen" waere fuer sie ein Befund ohne Handlung.
        const nurPlanung = !!user && getProjectRelation(p, user).isPlanningOnly;
        // SiFa = unbefristete Betreuung ohne Begehungsturnus: "Keine
        // Begehung seit X Tagen" waere dort ein Dauerbefund ohne Aussage
        // (docs/sifa-betreuung-plan.md).
        if (status === 'active' && projectStarted && !isReportExempt && !nurPlanung && !istSifaProjekt(meta)) {
            const interval = meta.intervalConfig;
            const daysStep = interval?.type === 'custom' ? (interval.customDays || 7) :
                interval?.type === 'daily' ? 1 :
                interval?.type === 'biweekly' ? 14 : 7;

            // Find last inspection date
            // Eine Pauschale ist keine Begehungsposition, auch mit „Begehung"
            // im Titel: ihre Monatsbuchung taeuschte sonst eine Begehung vor.
            // Die Begehung selbst steht im Pauschalprojekt als Nachweiszeile
            // auf der Pauschale und zaehlt ueber isProof.
            const inspectionServices = allServices.filter(s =>
                !isFlatRatePosition(s) &&
                (s.assignment === 'inspection' ||
                    (s.title || '').toLowerCase().includes('begehung'))
            );
            const inspectionServiceIds = new Set(inspectionServices.map(s => s.id));
            const isProof = inspectionProofMatcher(allServices);

            let lastInspectionDate: Date | null = null;

            // Check serviceLog
            const serviceLog = global?.serviceLog as ServiceTransaction[] | undefined;
            if (Array.isArray(serviceLog)) {
                serviceLog.forEach(entry => {
                    if ((inspectionServiceIds.has(entry.serviceId) || isProof(entry)) && entry.date && !isAccountingEntry(entry)) {
                        const d = new Date(entry.date);
                        if (!isNaN(d.getTime()) && (!lastInspectionDate || d > lastInspectionDate)) {
                            lastInspectionDate = d;
                        }
                    }
                });
            }

            // Also check last_inspection_date from DB
            const dbLastDate = p.last_inspection_date ? new Date(p.last_inspection_date) : null;
            if (dbLastDate && !isNaN(dbLastDate.getTime()) && (!lastInspectionDate || dbLastDate > lastInspectionDate)) {
                lastInspectionDate = dbLastDate;
            }

            if (lastInspectionDate) {
                const daysSince = Math.ceil((now.getTime() - lastInspectionDate.getTime()) / 864e5);
                const threshold = daysStep * 2; // 2× the interval = overdue

                if (daysSince > threshold) {
                    tasks.push({
                        id: 'inspection_overdue',
                        label: `Keine Begehung seit ${daysSince} Tagen`,
                        type: 'inspection_overdue',
                        priority: daysSince > threshold * 1.5 ? 'urgent' : 'soon',
                        detail: `Intervall: alle ${daysStep} Tage — letzte am ${lastInspectionDate.toLocaleDateString('de-DE')}`,
                        daysInfo: daysSince,
                    });
                }
            } else if (startDate) {
                // No inspection ever done on an active project
                const daysSinceStart = Math.ceil((now.getTime() - startDate.getTime()) / 864e5);
                if (daysSinceStart > daysStep) {
                    tasks.push({
                        id: 'inspection_never',
                        label: 'Noch keine Begehung durchgeführt',
                        type: 'inspection_overdue',
                        priority: daysSinceStart > daysStep * 2 ? 'urgent' : 'soon',
                        detail: `Projekt aktiv seit ${daysSinceStart} Tagen`,
                        daysInfo: daysSinceStart,
                    });
                }
            }
        }


        // Only add project if it has tasks (or dismissed tasks when showing them)
        if (tasks.length > 0 || dismissedTasks.length > 0) {
            // Sort tasks: urgent first, then soon, then normal
            tasks.sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);

            const urgentCount = tasks.filter(t => t.priority === 'urgent').length;
            const soonCount = tasks.filter(t => t.priority === 'soon').length;

            groups.push({
                project: p,
                projectName: p.name || meta.projectName || 'Unbenannt',
                projectNumber: meta.projectNumber || p.project_number || '-',
                coordinator: meta.coordinator || p.coordinator_name || '-',
                startDate: startDateStr || null,
                tasks,
                dismissedTasks,
                urgentCount,
                soonCount,
            });
        }
    });

    // Sort project groups: most urgent first
    return groups.sort((a, b) => {
        if (a.urgentCount !== b.urgentCount) return b.urgentCount - a.urgentCount;
        if (a.soonCount !== b.soonCount) return b.soonCount - a.soonCount;
        return a.projectName.localeCompare(b.projectName);
    });
};

// ═══════════════════════════════════════════════════════════════════
// 3. ZAEHLEN UND FLACHKLOPFEN — fuer Kacheln und Kurzlisten
// ═══════════════════════════════════════════════════════════════════

export interface TaskTotals {
    total: number;
    urgent: number;
    soon: number;
    normal: number;
    dismissed: number;
}

export const countTasks = (groups: ReadonlyArray<ProjectTaskGroup>): TaskTotals => {
    const total = groups.reduce((s, g) => s + g.tasks.length, 0);
    const urgent = groups.reduce((s, g) => s + g.urgentCount, 0);
    const soon = groups.reduce((s, g) => s + g.soonCount, 0);
    const dismissed = groups.reduce((s, g) => s + g.dismissedTasks.length, 0);
    return { total, urgent, soon, normal: total - urgent - soon, dismissed };
};

export interface FlatTask {
    group: ProjectTaskGroup;
    task: Task;
}

/**
 * Alle offenen Aufgaben als eine Liste, dringendste zuerst; bei gleicher
 * Stufe bleibt die Projektreihenfolge (die ist schon nach Dringlichkeit
 * sortiert). NEP-Positionen (ausgeblendet) sind nicht dabei.
 */
export const flattenTasks = (groups: ReadonlyArray<ProjectTaskGroup>): FlatTask[] => {
    const out: FlatTask[] = [];
    groups.forEach(group => group.tasks.forEach(task => out.push({ group, task })));
    return out.sort((a, b) => PRIORITY_ORDER[a.task.priority] - PRIORITY_ORDER[b.task.priority]);
};
